import { useEffect, useRef, useState } from 'preact/hooks';
import { ConfigContext, useConfigContext } from '../contexts/configContext';
import { ApiProvider } from '../contexts/apiContext';
import { BannersConfig, CommonPaymentBannerTrackingAttributes } from '../types';
import { useApi, AtRiskPayment } from '../api';
import { UpdatePaymentButton } from './UpdatePaymentButton';
import { cx } from '../utils/classnames';
import { CheckmarkIcon } from './CheckmarkIcon';
import { DismissButton } from './DismissButton';
import { isPaymentAlertDismissed, markPaymentAlertDismissed } from './dismissalStorage';
import { usePaymentBannerTracking } from './tracking';

type FetchState = { status: 'loading' } | { status: 'loaded'; atRiskPayments: AtRiskPayment[] } | { status: 'error' };

type BannerState = 'pending-action' | 'email-sent' | 'email-error';

function PaymentAlertInner() {
  const api = useApi();
  const config = useConfigContext();
  const [fetchState, setFetchState] = useState<FetchState>({ status: 'loading' });

  const [bannerState, setBannerState] = useState<BannerState>('pending-action');

  const [dismissed, setDismissed] = useState(() => isPaymentAlertDismissed());

  const paymentRef = useRef<HTMLDivElement>(null);
  const atRiskPayment =
    fetchState.status === 'loaded' && fetchState.atRiskPayments.length > 0 ? fetchState.atRiskPayments[0] : null;

  const track = usePaymentBannerTracking({ atRiskPayment, config });

  useEffect(() => {
    if (dismissed) return;
    api
      .getAtRiskPayments()
      .then(atRiskPayments => setFetchState({ status: 'loaded', atRiskPayments }))
      .catch(() => setFetchState({ status: 'error' }));
  }, [api, dismissed]);

  if (dismissed || !atRiskPayment) {
    return null;
  }

  const paymentText = getPaymentText(bannerState, atRiskPayment);

  const handleDismiss = () => {
    track('paymentbanner_dismissed');
    markPaymentAlertDismissed();
    setDismissed(true);
  };

  return (
    <div class="payment-wrapper">
      <div
        className={cx('payment-alert', bannerState === 'email-sent' && 'payment-alert--success')}
        data-testid="payment-alert"
        role="alert"
        tabIndex={-1}
        ref={paymentRef}
      >
        <span className="message">
          {bannerState === 'email-sent' && <CheckmarkIcon />}
          <span>{paymentText}</span>
        </span>
        {(bannerState === 'pending-action' || bannerState === 'email-error') && (
          <UpdatePaymentButton
            paymentId={atRiskPayment.payment}
            onClick={() => track('paymentbanner_cta_click')}
            onEmailSuccess={() => {
              setBannerState('email-sent');
              // move focus to the banner since the button will be removed
              paymentRef.current?.focus();
            }}
            onEmailError={() => setBannerState('email-error')}
          />
        )}
        <DismissButton onDismiss={handleDismiss} />
      </div>
    </div>
  );
}

export function getMessageDetails(payment: AtRiskPayment): {
  messageType: CommonPaymentBannerTrackingAttributes['messageType'];
  message: string;
} {
  if (payment.risk.reasons.includes('card_expiry') && !payment.risk.reasons.includes('payment_failure')) {
    return {
      messageType: 'card_expiring',
      message: 'Your payment method will expire before your next subscription order is placed.'
    };
  }
  return {
    messageType: 'default',
    message: 'Your last subscription order failed due to a problem with your payment method.'
  };
}

function getPaymentText(bannerState: BannerState, atRiskPayment: AtRiskPayment) {
  switch (bannerState) {
    case 'pending-action':
      return getMessageDetails(atRiskPayment).message;
    case 'email-sent':
      return 'Please check your email for next steps on updating your payment method.';
    case 'email-error':
      return "We couldn't send you the email to update your payment method. Please try again.";
  }
}

export function PaymentAlert({ config }: { config: BannersConfig }) {
  return (
    <ConfigContext.Provider value={config}>
      <ApiProvider>
        <PaymentAlertInner />
      </ApiProvider>
    </ConfigContext.Provider>
  );
}
